import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  ArrowForward,
  CheckCircle,
  Rocket,
  TrendingUp,
  Security,
  Speed,
} from '@mui/icons-material';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
`;

const NewCTASection = ({ onGetStarted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)';

  const sectionBg = isDark
    ? 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)';

  const cardBg = isDark ? alpha('#1e293b', 0.4) : alpha('#ffffff', 0.15);
  const textColor = '#ffffff';

  const benefits = [
    { icon: <Rocket />, text: 'Get started in 5 minutes' },
    { icon: <Security />, text: 'Bank-level security' },
    { icon: <Speed />, text: '10x faster operations' },
    { icon: <TrendingUp />, text: 'Proven ROI increase' },
  ];

  return (
    <Box
      sx={{
        background: sectionBg,
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        color: textColor,
      }}
    >
      {/* Background Decorations */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1,
          background: `
            radial-gradient(circle at 20% 30%, ${alpha('#fff', 0.3)} 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${alpha('#fff', 0.3)} 0%, transparent 50%)
          `,
        }}
      />

      {/* Floating Elements */}
      {[...Array(15)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: Math.random() * 6 + 2,
            height: Math.random() * 6 + 2,
            borderRadius: '50%',
            background: alpha('#fff', 0.3),
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `${float} ${Math.random() * 8 + 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={7}>
            <Stack spacing={4}>
              <Chip
                label="Limited Time Offer"
                sx={{
                  background: alpha('#fff', 0.2),
                  color: textColor,
                  fontWeight: 700,
                  border: `1px solid ${alpha('#fff', 0.3)}`,
                  alignSelf: 'flex-start',
                  px: 2,
                  py: 2.5,
                }}
              />

              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  fontFamily: '"Sora", "Inter", sans-serif',
                  fontWeight: 900,
                  lineHeight: 1.2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Ready to Transform Your Email Outreach?
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  lineHeight: 1.8,
                  opacity: 0.95,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                }}
              >
                Join thousands of freight professionals who have already revolutionized their 
                carrier outreach with Email Studio. Start your free trial today—no credit card required.
              </Typography>

              {/* Benefits Grid */}
              <Grid container spacing={2}>
                {benefits.map((benefit, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          background: alpha('#fff', 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${alpha('#fff', 0.3)}`,
                        }}
                      >
                        {React.cloneElement(benefit.icon, {
                          sx: { fontSize: 24, color: textColor },
                        })}
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      >
                        {benefit.text}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              {/* CTA Buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onGetStarted}
                  endIcon={<ArrowForward />}
                  sx={{
                    background: '#fff',
                    color: isDark ? '#6366f1' : '#3b82f6',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    py: 2,
                    px: 4,
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: `0 12px 40px ${alpha('#000', 0.3)}`,
                    transition: 'all 0.3s',
                    '&:hover': {
                      background: alpha('#fff', 0.95),
                      transform: 'translateY(-3px)',
                      boxShadow: `0 16px 50px ${alpha('#000', 0.4)}`,
                    },
                  }}
                >
                  Start Free Trial
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: alpha('#fff', 0.5),
                    color: textColor,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    py: 2,
                    px: 4,
                    borderRadius: 3,
                    borderWidth: 2,
                    textTransform: 'none',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#fff',
                      bgcolor: alpha('#fff', 0.1),
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  Schedule Demo
                </Button>
              </Stack>

              {/* Trust Indicators */}
              <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                <CheckCircle sx={{ fontSize: 20 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  14-day free trial
                </Typography>
                <Typography sx={{ mx: 1, opacity: 0.5 }}>•</Typography>
                <CheckCircle sx={{ fontSize: 20 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  No credit card required
                </Typography>
                <Typography sx={{ mx: 1, opacity: 0.5 }}>•</Typography>
                <CheckCircle sx={{ fontSize: 20 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Cancel anytime
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          {/* Right Content - Stats Cards */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              {[
                { value: '500+', label: 'Companies Trust Us', color: '#3b82f6' },
                { value: '50K+', label: 'Emails Sent Daily', color: '#8b5cf6' },
                { value: '98%', label: 'Deliverability Rate', color: '#ec4899' },
              ].map((stat, idx) => (
                <Card
                  key={idx}
                  sx={{
                    background: cardBg,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha('#fff', 0.2)}`,
                    borderRadius: 3,
                    p: 3,
                    transition: 'all 0.3s',
                    animation: `${float} ${6 + idx}s ease-in-out infinite`,
                    animationDelay: `${idx * 0.5}s`,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 60px ${alpha('#000', 0.3)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        color: textColor,
                        fontFamily: '"Sora", "Inter", sans-serif',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: alpha(textColor, 0.9),
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default NewCTASection;
